import React, { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  TextField,
  Menu,
  MenuItem,
  IconButton,
} from '@material-ui/core';
import { Formik } from 'formik';
import { format } from 'date-fns';
import { useHistory, useLocation } from 'react-router-dom';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { useRootStore } from '../../stores/BridgeProvider';
import {
  StyledForm,
  ErrorText,
  ButtonFlex,
  FormRow,
  RowItem,
} from '../styles/styles';
import Spinner from '../util/Spinner';
import {
  VoucherStatusEnums,
  VoucherTypeIdEnums,
  VoucherStatusTypeEnums,
} from '../common/Enums';
import {
  MenuList,
  filterOptions,
  ListboxComponent,
} from '../util/ReactSelectOverrides';
import { VoucherSchema } from '../../validation';
import SimpleSelect from '../util/SimpleSelect';
import { safeEval, safeFalsy } from '../util/UtilFunctions';
import { useSnackBars } from '../../alerts/SnackBarProvider';
import { COLORS } from '../util/Colors';
import {
  CustomCreatableSelect,
  CustomSelect,
} from '../util/CustomCreatableSelect';
import AccountSelect from '../util/AccountSelect';
import ACDropdown from '../util/ACDropdown';
import AccountSelectAC from '../util/AccountSelectAC';

// onSubmit is required
// buttons are custom elements to render at bottom, they're usually buttons
// type determines if certain fields need to be ommitted and also determines what options are
// available for selection in type
// TODO: type

const VoucherForm = ({
  onSubmit,
  voucher = {},
  buttons,
  menuItems,
  type,
  submitLabel = 'Submit',
}) => {
  const { state } = useLocation();
  const { dataStore, authStore, orgStore } = useRootStore();
  const [isLoading, setLoading] = useState(true);
  const [isCreating, setCreating] = useState(false);
  const { contacts, transactionCategories, accounts } = dataStore;
  const [anchorEl, setAnchorEl] = useState(null);
  const handleAlert = useSnackBars();
  const open = Boolean(anchorEl);

  const filter = createFilterOptions({
    limit: 80,
  });

  const handleClick = e => {
    e.stopPropagation;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isDP = type === 'DP';

  useEffect(() => {
    const initialize = async () => {
      const requests = [];
      if (contacts.length === 0) {
        requests.push(dataStore.retrieveContacts());
      }
      if (transactionCategories.length === 0) {
        requests.push(dataStore.retrieveTransactionCategories());
      }
      if (accounts.length === 0) {
        requests.push(dataStore.retrieveAccounts());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);

  if (isLoading) return <Spinner />;

  const vtEnumsArray = Object.keys(VoucherTypeIdEnums).map(key => [
    Number(key),
    VoucherTypeIdEnums[key],
  ]);

  const vtEnumsOptions = isDP
    ? vtEnumsArray.slice(4, 6)
    : vtEnumsArray.slice(0, 4);

  const voucherTypeDefault = (() => {
    const vT = ['', ''];
    if (!state) return vT;
    const { voucherType: vType } = state;
    switch (true) {
      case isDP && vType === 'paySomeone':
        return vtEnumsArray[5];
      case isDP && vType === 'collectPayment':
        return vtEnumsArray[4];
      case vType === 'paySomeone':
        return vtEnumsArray[1];
      case vType === 'collectPayment':
        return vtEnumsArray[0];
      default:
        return vT;
    }
  })();

  const handleInitialDate = dateType => {
    if (!voucher.VoucherId) {
      return format(new Date(), 'YYYY-MM-DD');
    }
    return dateType ? format(dateType, 'YYYY-MM-DD') : null;
  };

  const handleInitialDueDate = date => {
    const vType = safeFalsy(voucher.VoucherTypeId) || voucherTypeDefault[0];
    if (vType > 4) return null;
    return handleInitialDate(date);
  };

  const invalidEdits = edits => {
    // if the voucher is being edited and not created
    if (voucher) {
      // if the voucher had a status of approved or higher
      if (voucher.StatusId && voucher.StatusId > 2) {
        // if user is trying to edit account of approved voucher
        if (voucher.AccountId !== edits.AccountId) {
          // RAISE ERROR
          handleAlert(
            'Cannot edit account on invoice of status "Approved" or higher',
            COLORS.red,
          );
          return true;
        }
      }
    }

    return false;
  };

  return (
    <Formik
      validationSchema={VoucherSchema}
      initialValues={{
        ContactId: safeFalsy(voucher.ContactId),
        Contact: dataStore.getContactFromId(voucher.ContactId),
        ContactName: safeFalsy(voucher.ContactName),
        Description: safeFalsy(voucher.Description),
        DueDate: handleInitialDueDate(voucher.DueDate),
        VoucherAccountCode: safeFalsy(voucher.VoucherAccountCode),
        VoucherAccountName: safeFalsy(voucher.VoucherAccountName),
        VoucherDate: handleInitialDate(voucher.VoucherDate),
        VoucherNumber: safeFalsy(voucher.VoucherNumber, 'AUTO-GENERATE'),
        StatusId: safeFalsy(voucher.StatusId) || 0,
        VoucherTran: safeFalsy(voucher.VoucherTran, []),
        VoucherTypeDesc:
          safeFalsy(voucher.VoucherTypeDesc) || voucherTypeDefault[1],
        VoucherTypeId:
          safeFalsy(voucher.VoucherTypeId) || voucherTypeDefault[0],
        VoucherId: safeFalsy(voucher.VoucherId, null),
        Payments: safeFalsy(voucher.Payments),
        AccountId: safeFalsy(voucher.AccountId),
        Account: dataStore.getAccountFromId(voucher.AccountId),
      }}
      onSubmit={async (values, actions) => {
        if (invalidEdits(values)) {
          actions.setSubmitting(false);
          return;
        }
        await onSubmit(values);

        actions.setSubmitting(false);
      }}
      render={({
        handleBlur,
        handleChange,
        errors,
        handleSubmit,
        values,
        isSubmitting,
        setFieldValue,
        touched,
        dirty,
      }) => {
        const { VoucherTypeId } = values;
        const curVoucherType = (() =>
          vtEnumsArray.find(e => e[0] === values.VoucherTypeId))();

        const showError = val => errors[val] && touched[val];

        const handleCreateContact = async inputValue => {
          setCreating(true);
          try {
            const { data: contactData } = await authStore.request.post(
              `${process.env.REACT_APP_API}/api/contact`,
              {
                Name: inputValue,
                IsAgent: 0,
              },
            );
            const { id: cId } = contactData;
            await dataStore.retrieveContacts();
            const contact = dataStore.getContactFromId(cId);
            setFieldValue('Contact', contact);
            setFieldValue('ContactId', cId);
          } catch (e) {
            console.error(e);
          }
          setCreating(false);
        };

        const isValidAccountGroup = (TypeId, BankAccountType) => {
          if ([1, 3].includes(VoucherTypeId)) {
            return [1].includes(TypeId);
          }
          if ([2, 4].includes(VoucherTypeId)) {
            return [2].includes(TypeId);
          }
          if ([5, 6].includes(VoucherTypeId)) {
            return Boolean(BankAccountType);
          }
          return [1, 2].includes(TypeId);
        };

        const voidOrDeleted = !!voucher.VoucherId && voucher.StatusId >= 5;

        const voucherTypeIsDP = VoucherTypeId && VoucherTypeId > 4;

        const restrictedTypes = () => {
          const restricted = [];
          if (voucher.VoucherId) {
            switch (voucher.StatusId) {
              case 0:
                restricted.push(4, 5);
                break;
              case 1:
              case 2:
                restricted.push(0, 4, 5);
                break;
              case 3:
                restricted.push(0, 1, 2, 6);
                break;
              case 4:
                restricted.push(0, 1, 2, 3, 6);
                break;
              case 5:
                restricted.push(0, 1, 2, 3, 4, 6);
                break;
              case 6:
                restricted.push(0, 1, 2, 3, 4, 5);
                break;
              default:
            }
          } else {
            // is new voucher
            restricted.push(3, 4, 5, 6);
          }
          return restricted;
        };

        return (
          <div style={{ width: '100%' }} className="voucherFormStep">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Typography
                variant="h2"
                gutterBottom
                style={{ margin: '0 auto' }}
              >
                {voucher.VoucherId ? `Edit Transaction` : `New Transaction`}
              </Typography>
              {menuItems && (
                <div>
                  <IconButton size="small" onClick={handleClick}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu anchorEl={anchorEl} onClose={handleClose} open={open}>
                    {menuItems.map(item => item)}
                  </Menu>
                </div>
              )}
            </div>
            <StyledForm onSubmit={handleSubmit}>
              <div style={{ width: '100%', zIndex: 1002 }}>
                <ACDropdown
                  label={
                    <div>
                      Contact <span style={{ color: 'red' }}>*</span>
                    </div>
                  }
                  value={values.Contact}
                  handleHomeEndKeys
                  ListboxComponent={ListboxComponent}
                  options={contacts}
                  getOptionLabel={({ Name }) => Name || null}
                  getOptionSelected={(option, val) =>
                    option.ContactId === val.ContactId
                  }
                  renderOption={({ isNew, Name }) =>
                    isNew ? `Add "${Name}"` : Name
                  }
                  onChange={async (event, newValue) => {
                    if (newValue !== null && newValue.ContactId) {
                      setFieldValue('Contact', newValue);
                      setFieldValue(
                        'ContactId',
                        safeEval(() => newValue.ContactId),
                        null,
                      );
                    } else if (newValue && newValue.inputValue) {
                      // Create a new value from the user input
                      const newId = await handleCreateContact(
                        newValue.inputValue,
                      );
                      handleChange('ContactId', newId);
                    }
                  }}
                  filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    const inputValueIsNotAContact =
                      options.findIndex(
                        option => option.Name === params.inputValue,
                      ) < 0;

                    // Suggest the creation of a new value
                    if (params.inputValue !== '' && inputValueIsNotAContact) {
                      filtered.push({
                        inputValue: params.inputValue,
                        Name: params.inputValue,
                        isNew: true,
                      });
                    }

                    return filtered;
                  }}
                />
              </div>

              {showError('ContactId') && (
                <ErrorText>{errors.ContactId}</ErrorText>
              )}
              <FormRow>
                <RowItem>
                  <div style={{ width: '100%', zIndex: 1001 }}>
                    <ACDropdown
                      options={vtEnumsOptions}
                      value={curVoucherType ? { ...curVoucherType } : null}
                      style={{ width: 'inherit', margin: '16px 0 8px' }}
                      getOptionLabel={option => option[1]}
                      getOptionSelected={(option, value) => {
                        return option[0] === value[0];
                      }}
                      label={
                        <div>
                          Voucher Type <span style={{ color: 'red' }}>*</span>
                        </div>
                      }
                      onChange={(e, val) => {
                        setFieldValue(
                          'VoucherTypeId',
                          safeEval(() => val[0]),
                          null,
                        );
                        setFieldValue(
                          'VoucherTypeDesc',
                          safeEval(() => val[1]),
                          null,
                        );
                        setFieldValue('Account', null);
                        setFieldValue('AccountId', null);

                        if (safeEval(() => val[0]) > 4) {
                          setFieldValue('DueDate', null);
                        }
                      }}
                    />
                  </div>
                  {showError('VoucherTypeId') && (
                    <ErrorText>{errors.VoucherTypeId}</ErrorText>
                  )}
                </RowItem>
                <RowItem>
                  <div style={{ width: '100%', zIndex: 1000 }}>
                    {/* ****** fix uncontrolled value warning */}
                    <AccountSelectAC
                      value={values.Account || null}
                      isValidAccountGroup={isValidAccountGroup}
                      changeMemoVals={VoucherTypeId}
                      style={{ width: 'inherit', margin: '16px 0 8px' }}
                      label={
                        <div>
                          Account <span style={{ color: 'red' }}>*</span>
                        </div>
                      }
                      onChange={(e, val) => {
                        setFieldValue('Account', val);
                        setFieldValue(
                          'AccountId',
                          safeEval(() => val.AccountId),
                          null,
                        );
                      }}
                    />
                  </div>
                  {showError('AccountId') && (
                    <ErrorText>{errors.AccountId}</ErrorText>
                  )}
                </RowItem>
              </FormRow>
              <FormRow>
                <RowItem>
                  <TextField
                    name="VoucherDate"
                    label={
                      <div>
                        Voucher Date <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    type="date"
                    value={values.VoucherDate}
                    margin="normal"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {showError('VoucherDate') && (
                    <ErrorText style={{ textAlign: 'right' }}>
                      {errors.VoucherDate}
                    </ErrorText>
                  )}
                </RowItem>
                <RowItem>
                  <TextField
                    name="DueDate"
                    label={
                      <div>
                        Due Date{' '}
                        {!voucherTypeIsDP && (
                          <span style={{ color: 'red' }}>*</span>
                        )}
                      </div>
                    }
                    type="date"
                    value={values.DueDate || ''}
                    margin="normal"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    disabled={voucherTypeIsDP}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {showError('DueDate') && (
                    <ErrorText style={{ textAlign: 'right' }}>
                      {errors.DueDate}
                    </ErrorText>
                  )}
                </RowItem>
              </FormRow>
              <FormRow>
                <RowItem>
                  <TextField
                    name="VoucherNumber"
                    label={
                      <div>
                        Voucher Number <span style={{ color: 'red' }}>*</span>
                      </div>
                    }
                    value={values.VoucherNumber}
                    margin="normal"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                    fullWidth
                    error={showError('VoucherNumber')}
                  />
                  {showError('VoucherNumber') && (
                    <ErrorText style={{ textAlign: 'right' }}>
                      {errors.VoucherNumber}
                    </ErrorText>
                  )}
                </RowItem>
                <RowItem>
                  <ACDropdown
                    label="Voucher Status"
                    value={VoucherStatusTypeEnums.find(
                      status => status.val === values.StatusId,
                    )}
                    style={{ width: 'inherit', margin: '16px 0 8px' }}
                    options={VoucherStatusTypeEnums.filter(
                      t => !restrictedTypes().includes(t.val),
                    )}
                    getOptionLabel={option => option.Name}
                    getOptionSelected={(option, value) => {
                      if (value === '') {
                        return true;
                      }
                      if (option.val === value.val) {
                        return true;
                      }
                    }}
                    onChange={(e, v) => {
                      setFieldValue('StatusId', v.val);
                    }}
                  />
                  {showError('StatusId') && (
                    <ErrorText style={{ textAlign: 'right' }}>
                      {errors.StatusId}
                    </ErrorText>
                  )}
                </RowItem>
              </FormRow>

              <TextField
                name="Description"
                label="Description"
                value={values.Description}
                margin="normal"
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outlined"
                fullWidth
                error={showError('Description')}
              />
              <ButtonFlex>
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  size="large"
                  disabled={!dirty || isSubmitting}
                >
                  {submitLabel}
                </Button>
                {buttons && buttons.map(button => button)}
              </ButtonFlex>
            </StyledForm>
          </div>
        );
      }}
    />
  );
};

export default VoucherForm;
